/**
 * Routes whose content is English-only (no per-locale translation).
 *
 * KEEP IN SYNC BY HAND with scripts/routes.js `NON_LOCALIZED_ROUTES` — that
 * file is the Node/CommonJS copy used by prerender + sitemap; this is the
 * ESM copy used by the running app. Adding/removing a translated page means
 * updating both.
 *
 * Used to stop the app emitting locale-prefixed links (e.g. /es/security) to
 * pages that only exist in English. Those phantom URLs get crawled and flagged
 * by Search Console as "Alternate page with proper canonical tag".
 */
export const NON_LOCALIZED_ROUTES = new Set([
  "/features/import",
  "/features/offline",
  "/features/duplicate-detection",
  "/features/mcp",
  "/docs",
  "/legal/terms-of-service",
  "/legal/privacy-policy",
  "/legal/extension-privacy-policy",
  "/blog",
  // Bespoke, English-content competitor pages (Raindrop/Pocket ARE localized).
  // Without these, hreflang emitted phantom /es/instapaper-alternative/ etc.
  // that GSC flagged as "Crawled - currently not indexed".
  "/instapaper-alternative",
  "/omnivore-alternative",
  // English-only exact-match keyword URL (canonicals to /bookmarks-for-schools).
  "/bookmark-organizer-for-schools",
]);

/**
 * True if a base path (locale-less, e.g. "/security" or "/blog/my-post") maps
 * to English-only content — either an exact non-localized route or a sub-path
 * of one (blog posts, legal sub-pages, docs sections).
 * @param {string} basePath
 * @returns {boolean}
 */
export const isNonLocalizedPath = (basePath) => {
  if (!basePath) return false;
  if (NON_LOCALIZED_ROUTES.has(basePath)) return true;
  return [...NON_LOCALIZED_ROUTES].some(
    (route) => route !== "/" && basePath.startsWith(`${route}/`)
  );
};
